import type { CSSProperties } from 'react';
import type { LayoutHeadlineStyle, PostLayoutConfig } from './types';

export const SYSTEM_SANS = 'system-ui,-apple-system,sans-serif';

export function pickDefinedHeadlineStyle(c: LayoutHeadlineStyle): LayoutHeadlineStyle {
  const out: LayoutHeadlineStyle = {};
  if (c.headlineColor != null) out.headlineColor = c.headlineColor;
  if (c.headlineFontFamily != null) out.headlineFontFamily = c.headlineFontFamily;
  if (c.headlineFontUrl != null) out.headlineFontUrl = c.headlineFontUrl;
  if (c.headlineFontSize != null) out.headlineFontSize = c.headlineFontSize;
  if (c.headlineAnchor != null) out.headlineAnchor = c.headlineAnchor;
  return out;
}

export function layoutHeadlineCss(
  style: LayoutHeadlineStyle,
  fallback: { color: string; fontSize: number; fontFamily: string },
): CSSProperties {
  const css: CSSProperties = {
    color: style.headlineColor ?? fallback.color,
    fontSize: style.headlineFontSize ?? fallback.fontSize,
    fontFamily: style.headlineFontFamily ?? fallback.fontFamily,
    whiteSpace: 'pre-wrap',
  };
  if (style.headlineAnchor) {
    css.position = 'absolute';
    css.left = `${style.headlineAnchor.x * 100}%`;
    css.top = `${style.headlineAnchor.y * 100}%`;
    css.transform = 'translate(-50%, -50%)';
    css.width = '86%';
    css.margin = 0;
    css.zIndex = 2;
    css.textShadow = '0 1px 8px rgba(0,0,0,0.55), 0 0 1px rgba(255,255,255,0.85)';
  }
  return css;
}

export interface HeadlineDragDefaults {
  x: number;
  y: number;
  width: number;
  axis: 'xy' | 'y';
}

export function defaultHeadlineDrag(config: PostLayoutConfig): HeadlineDragDefaults {
  switch (config.kind) {
    case 'fact_quote':
      return { x: 0.5, y: 0.88, width: 0.86, axis: 'xy' };
    case 'model_x':
      return { x: 0.5, y: 0.22, width: 0.86, axis: 'xy' };
    case 'news_post':
      if (config.style === 'breaking_news') return { x: 0.5, y: 0.86, width: 0.86, axis: 'xy' };
      if (config.style === 'instagram') return { x: 0.5, y: 0.92, width: 0.86, axis: 'xy' };
      return { x: 0.5, y: 0.22, width: 0.86, axis: 'xy' };
    case 'fofoca':
      return { x: 0.72, y: 0.62, width: 0.38, axis: 'xy' };
    case 'satisfatorio':
      return { x: 0.5, y: 0.5, width: 0.7, axis: 'y' };
    default:
      return { x: 0.5, y: 0.5, width: 0.86, axis: 'xy' };
  }
}

export function resolveHeadlineAnchor(config: PostLayoutConfig): { x: number; y: number } {
  if ('headlineAnchor' in config && config.headlineAnchor) {
    return config.headlineAnchor;
  }
  return defaultHeadlineDrag(config);
}

export function canDragLayoutHeadline(config: PostLayoutConfig | null | undefined): boolean {
  if (!config) return false;
  switch (config.kind) {
    case 'model_x':
    case 'news_post':
    case 'fact_quote':
      return true;
    case 'fofoca':
      return Boolean((config.videoTitle ?? '').trim() || (config.handle ?? '').trim());
    case 'satisfatorio':
      return config.titleFrame !== 'none' && Boolean((config.title ?? '').trim());
    default:
      return false;
  }
}
